import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Medicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    stockQuantity: '',
    reorderLevel: '',
  });

  const fetchMedicines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/medicines`);
      setMedicines(response.data);
    } catch (error) {
      console.error('Error fetching medicines:', error);
    }
  }, []);

  useEffect(() => {
    fetchMedicines();
  }, [fetchMedicines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMedicine) {
        await axios.put(`${API}/medicines/${editingMedicine.id}`, formData);
      } else {
        await axios.post(`${API}/medicines`, formData);
      }
      fetchMedicines();
      resetForm();
    } catch (error) {
      console.error('Error saving medicine:', error);
    }
  };

  const handleEdit = (medicine) => {
    setEditingMedicine(medicine);
    setFormData({
      name: medicine.name,
      price: medicine.price,
      stockQuantity: medicine.stockQuantity,
      reorderLevel: medicine.reorderLevel,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', stockQuantity: '', reorderLevel: '' });
    setEditingMedicine(null);
    setShowForm(false);
  };

  const isLowStock = (medicine) => medicine.stockQuantity <= medicine.reorderLevel;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="medicines-title">
            Medicines
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage medicine inventory</p>
        </div>
        <button
          className="primary-btn flex items-center gap-2"
          onClick={() => setShowForm(true)}
          data-testid="add-medicine-btn"
        >
          <Plus size={20} />
          Add Medicine
        </button>
      </div>

      {showForm && (
        <div className="card p-6 mb-8" data-testid="medicine-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingMedicine ? 'Edit Medicine' : 'Add New Medicine'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Medicine Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="medicine-name-input"
                />
              </div>
              <div>
                <label>Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  data-testid="medicine-price-input"
                />
              </div>
              <div>
                <label>Stock Quantity</label>
                <input
                  type="number"
                  value={formData.stockQuantity}
                  onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                  required
                  data-testid="medicine-stock-input"
                />
              </div>
              <div>
                <label>Reorder Level</label>
                <input
                  type="number"
                  value={formData.reorderLevel}
                  onChange={(e) => setFormData({ ...formData, reorderLevel: e.target.value })}
                  required
                  data-testid="medicine-reorder-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="medicine-submit-btn">
                {editingMedicine ? 'Update' : 'Save'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="medicine-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="overflow-x-auto">
          <table data-testid="medicines-table">
            <thead>
              <tr>
                <th>Medicine Name</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Reorder Level</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {medicines.map((medicine) => (
                <tr key={medicine.id} data-testid={`medicine-row-${medicine.id}`}>
                  <td className="font-medium" style={{ color: '#2D3A2A' }}>{medicine.name}</td>
                  <td>₹{medicine.price}</td>
                  <td>{medicine.stockQuantity}</td>
                  <td>{medicine.reorderLevel}</td>
                  <td>
                    {isLowStock(medicine) ? (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                        data-testid={`low-stock-badge-${medicine.id}`}
                      >
                        <AlertTriangle size={14} />
                        Low Stock
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                      >
                        In Stock
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleEdit(medicine)}
                      className="p-2 rounded hover:bg-gray-100 transition-colors"
                      data-testid={`edit-medicine-${medicine.id}`}
                    >
                      <Edit size={18} style={{ color: '#990099' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {medicines.length === 0 && (
            <div className="text-center py-8" style={{ color: '#6B7280' }} data-testid="no-medicines">
              No medicines found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Medicines;
